import { useAuthStore } from '../store/auth';

const API_BASE = '/api';

interface RequestOptions extends RequestInit {
  skipAuth?: boolean;
}

class ApiClient {
  private getAuthHeader(): Record<string, string> {
    const token = useAuthStore.getState().token;
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  async request<T>(
    endpoint: string,
    options: RequestOptions = {}
  ): Promise<{ data: T; status: number }> {
    const { skipAuth = false, headers = {}, ...restOptions } = options;

    const hasBody = restOptions.body !== undefined;
    const response = await fetch(`${API_BASE}${endpoint}`, {
      ...restOptions,
      headers: {
        ...(hasBody ? { 'Content-Type': 'application/json' } : {}),
        ...(skipAuth ? {} : this.getAuthHeader()),
        ...headers,
      },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Request failed' }));
      throw new ApiError(response.status, error.message || 'Request failed');
    }

    const data = await response.json();
    return { data, status: response.status };
  }

  async get<T>(endpoint: string, options?: RequestOptions) {
    return this.request<T>(endpoint, { ...options, method: 'GET' });
  }

  async post<T>(endpoint: string, body?: unknown, options?: RequestOptions) {
    return this.request<T>(endpoint, {
      ...options,
      method: 'POST',
      body: JSON.stringify(body ?? {}),
    });
  }

  async put<T>(endpoint: string, body?: unknown, options?: RequestOptions) {
    return this.request<T>(endpoint, {
      ...options,
      method: 'PUT',
      body: JSON.stringify(body ?? {}),
    });
  }

  async delete<T>(endpoint: string, options?: RequestOptions) {
    return this.request<T>(endpoint, { ...options, method: 'DELETE' });
  }
}

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export const apiClient = new ApiClient();

// ---- Agent Factory API methods ----

export interface TemplateListResponse {
  industries: Array<{
    id: string;
    name: string;
    description: string;
    icon: string;
    defaultSkills: string[];
  }>;
  personas: Array<{
    id: string;
    name: string;
    description: string;
    icon: string;
    exampleMessages: Array<{ role: 'user' | 'assistant'; content: string }>;
  }>;
  skills: Array<{
    id: string;
    name: string;
    description: string;
    icon: string;
    triggers: string[];
    requires: string[];
  }>;
}

export interface GenerateResponse {
  status: string;
  message: string;
  files: string[];
}

export const factoryApi = {
  /** List all available templates */
  listTemplates: () =>
    apiClient.get<TemplateListResponse>('/agent/factory/templates'),

  /** Preview workspace files without deploying */
  preview: (spec: Record<string, unknown>) =>
    apiClient.post<{ files: Record<string, string> }>('/agent/factory/preview', spec),

  /** Generate workspace and deploy agent */
  generate: (spec: Record<string, unknown>) =>
    apiClient.post<GenerateResponse>('/agent/factory/generate', spec),
};
